import React, { useRef, useState } from 'react';
import moment from 'moment';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { DatePicker as MuiDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { Event } from '@material-ui/icons';
import { IconButton, InputAdornment, PaperProps, PopoverOrigin } from '@material-ui/core';
import DateFnsUtils from '@date-io/moment';
import useStyles, { ErrorMsg, HelperText } from './DatePicker.styles';
import { noop } from 'lodash';

export interface DatePickerProps {
  onChange?: (date: MaterialUiPickersDate) => void;
  dataQaId?: string;
  format?: string;
  minDate?: MaterialUiPickersDate;
  minDateMessage?: string;
  maxDate?: MaterialUiPickersDate;
  maxDateMessage?: string;
  placeholder?: string;
  value?: MaterialUiPickersDate;
  autoOk?: boolean;
  disabled?: boolean;
  onClose?: (date: MaterialUiPickersDate) => void;
  helperText?: string;
  errorMsg?: string;
}

const calendarDayHandler = (
  day: MaterialUiPickersDate,
  selectedDay: MaterialUiPickersDate,
  dayInCurrentMonth: boolean,
  dayComponent: JSX.Element,
) => {
  const {
    props: { children, current, disabled, hidden, selected },
  } = dayComponent;

  let qaId = `calendar-day-${children}`;
  if (current) qaId += '-current';
  if (disabled) qaId += '-disabled';
  if (hidden) qaId += ' -hidden';
  if (selected) qaId += '-selected';

  return React.cloneElement(dayComponent, {
    'data-qa-id': qaId,
  });
};

const DatePicker: React.FC<DatePickerProps> = ({
  minDate,
  minDateMessage,
  maxDate,
  maxDateMessage,
  placeholder,
  value,
  disabled,
  dataQaId,
  onClose = noop,
  onChange = noop,
  autoOk = false,
  format = 'DD MMMM YYYY',
  helperText,
  errorMsg,
}: DatePickerProps) => {
  const [selectedDate, setSelectedDate] = useState<MaterialUiPickersDate>(moment.parseZone(value) || null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const classes = useStyles({ isOpen });
  const inputRootEl = useRef<HTMLDivElement>(null);

  const onDateChange = (date: MaterialUiPickersDate): void => {
    if (date) {
      setSelectedDate(date);
      onChange(date);
    }
  };

  const onPickerClose = (): void => {
    setIsOpen(false);
    if (selectedDate) {
      onClose(selectedDate);
    }
  };

  const onOpen = (): void => setIsOpen(true);

  const InputProps = {
    classes: {
      root: classes.input,
    },
    fullWidth: true,
    ref: inputRootEl,
    readOnly: true,
    endAdornment: (
      <InputAdornment position="end">
        <IconButton aria-label="calendar-button" disabled={disabled}>
          <Event />
        </IconButton>
      </InputAdornment>
    ),
  };

  const PopoverProps = {
    anchorOrigin: { vertical: 'bottom', horizontal: 'right' } as PopoverOrigin,
    anchorEl: inputRootEl.current,
    transformOrigin: {
      vertical: 'top',
      horizontal: 'right',
    } as PopoverOrigin,
    classes: {
      paper: classes.paper,
    },
    PaperProps: {
      'data-qa-id': `calendar-${dataQaId}`,
    } as PaperProps,
  };

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <MuiDatePicker
        disableToolbar
        fullWidth
        variant="inline"
        autoOk={autoOk}
        format={format}
        margin="normal"
        InputProps={InputProps}
        inputVariant={'outlined'}
        id={`date-picker-${dataQaId}`}
        value={selectedDate || ''}
        placeholder={placeholder}
        minDate={minDate}
        minDateMessage={minDateMessage}
        maxDate={maxDate}
        maxDateMessage={maxDateMessage}
        onClose={onPickerClose}
        onChange={onDateChange}
        onOpen={onOpen}
        disabled={disabled}
        data-qa-id={`date-picker-${dataQaId}`}
        inputProps={{
          'aria-label': 'date-picker',
          'data-qa-id': `calendar-input-${dataQaId}`,
          style: { cursor: disabled ? 'default' : 'pointer' },
        }}
        PopoverProps={PopoverProps}
        renderDay={calendarDayHandler}
        leftArrowButtonProps={{ id: 'calendar-button-prev-month' }}
        rightArrowButtonProps={{ id: 'calendar-button-next-month' }}
        error={!!errorMsg}
        style={{ marginBottom: '0px' }}
      />
      <ErrorMsg data-qa-id={`date-picker-${dataQaId}-errorMsg`} className="errorMsg">
        {errorMsg}
      </ErrorMsg>
      <HelperText data-qa-id={`date-picker-${dataQaId}-helperText`} className="helperText">
        {helperText}
      </HelperText>
    </MuiPickersUtilsProvider>
  );
};

export default DatePicker;
